/**
 * Base HTTP client for all API calls.
 * Handles JWT cookies + CSRF, with localStorage Authorization-header
 * fallback retained for inter-app proxies that forward the caller's
 * header server-to-server.
 */
import { store } from '../state/store.js';
import { getActiveLocale, translate as t } from '../i18n/active.js';

const escText = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Support sub-path deployment (e.g. /hub/) via import.meta.env.BASE_URL (Vite)
// or window.__API_BASE__ (manual override). Falls back to '/api'.
const BASE_URL = (typeof window !== 'undefined' && window.__API_BASE__)
  || (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL
      ? import.meta.env.BASE_URL.replace(/\/$/, '') + '/api'
      : '/api');

// Refresh a quarter of the way before the access token dies, so a plain
// `<img>` — which carries only the cookie and never retries — is never
// painting against a lapsed session.
const REFRESH_SAFETY_MS = 30 * 1000;      // never cut it finer than this
const REFRESH_STALE_MS = 5 * 60 * 1000;   // "close enough to bother" on wake
const REFRESH_JITTER_MS = 4 * 60 * 1000;  // spread a building-wide wave
// Only used when the server gave us no expiry hint (an older session, or a
// token-issuing path that predates it). Deliberately well inside the
// shortest access-token life the estate ships (1 hour).
const FALLBACK_REFRESH_MS = 20 * 60 * 1000;

const CSRF_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The reader's language travels on every request, so the server can localise
 * what it says (isg_common server catalogues). It is the ACTIVE locale — en-GB
 * in an app that has not adopted i18n, which is right: a half-English app
 * should not get Czech error messages.
 */
const LOCALE_HEADER = 'X-ISG-Locale';

/**
 * ⚠️ Same-origin requests only. X-ISG-Locale is not a CORS-safelisted header,
 * so on a cross-origin call it forces a preflight, and every app's flask-cors
 * `allow_headers` is ['Content-Type', 'Authorization'] — the request would be
 * REFUSED by the browser, turning a language hint into an outage. Relative
 * paths (the norm) always qualify.
 */
function _sendsLocale(url) {
  if (!/^https?:/i.test(url)) return true;
  try {
    return typeof window !== 'undefined'
      && new URL(url, window.location.href).origin === window.location.origin;
  } catch (_) {
    return false;
  }
}

function _locale() {
  try { return getActiveLocale(); } catch (_) { return null; }
}

/**
 * `{ 'X-ISG-Locale': <code> }` for a component that must use raw `fetch`
 * (streaming responses: the agent widget, the ⓘ Ask tab, deck studio). Spread
 * it into that call's headers so the server answers in the reader's language.
 * Empty for a cross-origin URL, for the reason given on _sendsLocale().
 */
export function localeHeaders(url = '') {
  const loc = _locale();
  return loc && _sendsLocale(url) ? { [LOCALE_HEADER]: loc } : {};
}

function _readCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const c of document.cookie.split(';')) {
    const trimmed = c.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

export class ApiClient {
  constructor() {
    this._token = localStorage.getItem('jwt_token');
    this._refreshToken = localStorage.getItem('jwt_refresh_token');
    // Single shared in-flight refresh promise — concurrent 401s queue here
    // instead of stampeding /api/auth/refresh.
    this._refreshInFlight = null;
    this._refreshTimer = null;
    this._refreshFailed = false;
    this._keepAliveStarted = false;
    this._accessExpiresAt = null;
  }

  get token() {
    return this._token;
  }

  set token(value) {
    this._token = value;
    if (value) {
      localStorage.setItem('jwt_token', value);
    } else {
      localStorage.removeItem('jwt_token');
    }
  }

  get refreshToken() {
    return this._refreshToken;
  }

  set refreshToken(value) {
    this._refreshToken = value;
    if (value) {
      localStorage.setItem('jwt_refresh_token', value);
    } else {
      localStorage.removeItem('jwt_refresh_token');
    }
  }

  /**
   * True if either auth carrier is present — cookie-based csrf_access_token
   * (a tell that flask-jwt-extended set our auth cookie) or the legacy
   * localStorage token. Used by callers that need a cheap "should I try?"
   * signal before hitting the API.
   */
  hasAuth() {
    return Boolean(this._token) || Boolean(_readCookie('csrf_access_token'));
  }

  _clearAuth() {
    this.token = null;
    this.refreshToken = null;
    store.set('user', null);
    store.set('isAuthenticated', false);
  }

  _bounceToLogin() {
    const loginPath = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL)
      ? import.meta.env.BASE_URL.replace(/\/$/, '') + '/login'
      : '/login';
    if (!window.location.pathname.endsWith('/login')) {
      window.location.href = loginPath;
    }
  }

  /**
   * Attempt a refresh once. Concurrent callers share the same promise.
   * Resolves true on success (server rotates cookies + may return tokens
   * in JSON body during the transition), false on failure.
   */
  async _attemptRefresh() {
    // Refresh works as long as the refresh-cookie is alive on the receiving
    // origin — we don't need a localStorage refresh token. Older clients
    // that still have one in localStorage send it as Authorization too;
    // the server accepts either.
    if (this._refreshInFlight) return this._refreshInFlight;

    this._refreshInFlight = (async () => {
      try {
        const url = `${BASE_URL}/auth/refresh`;
        const headers = {};
        const loc = _locale();
        if (loc && _sendsLocale(url)) headers[LOCALE_HEADER] = loc;
        // CSRF: refresh cookie carries its own paired csrf_refresh_token.
        const csrfRefresh = _readCookie('csrf_refresh_token');
        if (csrfRefresh) {
          headers['X-CSRF-REFRESH-TOKEN'] = csrfRefresh;
        }
        // Legacy header fallback for clients still on localStorage refresh.
        if (this._refreshToken) {
          headers['Authorization'] = `Bearer ${this._refreshToken}`;
        }
        const resp = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers,
        });
        if (!resp.ok) return false;
        // Cookies are already rotated by the server response. JSON body is
        // present during the transition; consume it if available so any
        // localStorage clients also stay fresh.
        try {
          const data = await resp.json();
          if (data && data.access_token) this.token = data.access_token;
          if (data && data.refresh_token) this.refreshToken = data.refresh_token;
          this.noteAccessExpiry(data && data.access_expires_at);
        } catch (_) {
          // No body — the cookies still rotated, so keep the session alive on
          // the conservative default rather than giving up on scheduling.
          this.noteAccessExpiry(null);
        }
        this._refreshFailed = false;
        return true;
      } catch (_) {
        return false;
      } finally {
        this._refreshInFlight = null;
      }
    })();

    return this._refreshInFlight;
  }


  // ── Keeping the session alive BEFORE it dies ────────────────────────
  //
  // 🔴 This exists because of a bug that looked like nothing to do with
  // auth: images vanishing. The SPA survives an expired access token — it
  // gets a 401, refreshes, retries. A plain `<img src="/uploads/…">` cannot:
  // it carries only the cookie, and it NEVER RETRIES. So when the cookie
  // lapsed, every avatar, banner and thumbnail on the page 404'd silently
  // while the page itself kept working, until a hard reload. Measured on the
  // intranet: valid cookie 200, expired cookie 404, first seen 2026-08-07.
  //
  // ⚠️ It also moves refreshes onto a per-user rate-limit bucket. The
  // limiter reads the ACCESS token to identify a caller, so a refresh made
  // AFTER expiry has no readable identity and falls back to the client IP —
  // which the whole office shares through one NAT. Refreshing early keys on
  // the person instead.

  /** Record when the current access token dies, and arm the next refresh. */
  noteAccessExpiry(epochSeconds) {
    // ⚠️ A missing value means UNKNOWN, never "expires now". Not every
    // token-issuing path carries the hint, and an old session predates it
    // entirely — those fall back to a conservative fraction of the shortest
    // lifetime we ship, which is safe if pessimistic.
    this._accessExpiresAt = (typeof epochSeconds === 'number' && epochSeconds > 0)
      ? epochSeconds * 1000
      : null;
    try {
      if (this._accessExpiresAt) {
        localStorage.setItem('jwt_access_expires_at', String(this._accessExpiresAt));
      } else {
        localStorage.removeItem('jwt_access_expires_at');
      }
    } catch (_) { /* private mode — scheduling still works in-memory */ }
    this._armRefresh();
  }

  /** ms until we WANT to refresh: three-quarters through the token's life. */
  _msUntilRefresh() {
    const now = Date.now();
    if (this._accessExpiresAt) {
      const remaining = this._accessExpiresAt - now;
      // Refresh with a quarter of the life left, and never schedule into the
      // past — a token already past its refresh point wants one immediately.
      return Math.max(0, remaining - (remaining > 0 ? remaining * 0.25 : 0)
        - REFRESH_SAFETY_MS);
    }
    return FALLBACK_REFRESH_MS;
  }

  _armRefresh() {
    if (this._refreshTimer) { clearTimeout(this._refreshTimer); this._refreshTimer = null; }
    if (!this._sessionLooksLive()) return;
    let delay = this._msUntilRefresh();
    // ⚠️ JITTER. Without it every open tab in the building refreshes on the
    // same second — 250 requests in one tick, which is a stampede even when
    // each one is under its own per-user limit.
    delay += Math.floor(Math.random() * REFRESH_JITTER_MS);
    // setTimeout is capped at ~24.8 days and is not a wake-up call anyway;
    // visibilitychange below is what covers a sleeping laptop.
    this._refreshTimer = setTimeout(() => this._proactiveRefresh(), Math.max(1000, delay));
  }

  async _proactiveRefresh() {
    this._refreshTimer = null;
    if (!this._sessionLooksLive()) return;
    const ok = await this._attemptRefresh();
    if (ok) {
      // `noteAccessExpiry` re-armed us from the fresh token.
      if (!this._accessExpiresAt) this._armRefresh();
      return;
    }
    // ⚠️ Do NOT loop on failure. A refresh that fails usually means the
    // session is genuinely over; retrying on a timer turns one dead session
    // into a request every few seconds for as long as the tab is open.
    this._refreshFailed = true;
  }

  /** Cheap "is there plausibly a session here" test — never a security check. */
  _sessionLooksLive() {
    if (this._refreshFailed) return false;
    if (typeof document === 'undefined') return false;
    // The login page must never schedule anything.
    if (/\/login(\b|\/|$)/.test(window.location?.pathname || '')) return false;
    // The refresh cookie's CSRF partner is readable (it is deliberately not
    // httpOnly); its presence is the best signal available to JS that a
    // session exists at all.
    return !!(_readCookie('csrf_refresh_token') || _readCookie('csrf_access_token')
      || this._refreshToken);
  }

  /** Refresh now if we are close to (or past) the point of wanting to. */
  async refreshIfStale() {
    if (!this._sessionLooksLive()) return false;
    if (this._accessExpiresAt
        && Date.now() < this._accessExpiresAt - REFRESH_STALE_MS) return false;
    return this._proactiveRefresh().then(() => true);
  }

  /** Force one single-flight refresh, if a session plausibly exists.
      Used by image recovery, which cannot consult our expiry bookkeeping:
      an older session predates the `access_expires_at` hint entirely, so
      "not stale by our reckoning" and "the cookie works" are different
      claims and only the server can settle the second. */
  async recoverSession() {
    if (!this._sessionLooksLive()) return false;
    return this._attemptRefresh();
  }

  /** Start the keep-alive. Idempotent; safe to call from every app main. */
  startSessionKeepAlive() {
    if (this._keepAliveStarted) return;
    this._keepAliveStarted = true;
    // 🔴 The COOKIE first, localStorage second. A browser that arrived holding
    // cookies — any SSO landing, or simply a navigation — never saw the JSON
    // body that carries this, so without the cookie it would never learn its
    // own expiry and would sit on the conservative fallback interval for the
    // life of the session, refreshing several times more often than needed.
    // The cookie is a bare timestamp and deliberately not httpOnly.
    const fromCookie = Number(_readCookie('access_expires_at'));
    if (fromCookie > 0) {
      this._accessExpiresAt = fromCookie * 1000;
    } else {
      try {
        const stored = Number(localStorage.getItem('jwt_access_expires_at'));
        if (stored > 0) this._accessExpiresAt = stored;
      } catch (_) { /* private mode */ }
    }

    // 🔴 A timer does not survive a sleeping laptop, and waking up is exactly
    // when the cookie is stalest and the page repaints every image at once.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshIfStale();
    });
    window.addEventListener('online', () => this.refreshIfStale());
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.refreshIfStale(); });

    this._armRefresh();
  }

  async request(path, options = {}) {
    const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
    return this._send(url, options, /*allowRetry=*/true);
  }

  async _send(url, options, allowRetry) {
    const headers = { ...options.headers };
    const method = (options.method || 'GET').toUpperCase();

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    // Authorization header is the legacy carrier — kept so server-to-server
    // proxies (e.g. intranet → helpdesk) that forward the inbound header
    // keep working. The httpOnly auth cookie is sent automatically via
    // credentials:'include' regardless.
    if (this._token) {
      headers['Authorization'] = `Bearer ${this._token}`;
    }

    // An explicit per-call value wins (e.g. a proxy relaying someone else's).
    const loc = _locale();
    if (loc && !headers[LOCALE_HEADER] && _sendsLocale(url)) headers[LOCALE_HEADER] = loc;

    // CSRF: when the cookie is present and we're issuing a state-changing
    // request, send the matching X-CSRF-TOKEN header. Cookie-only
    // (header-less) callers are exempted server-side.
    if (CSRF_METHODS.has(method)) {
      const csrf = _readCookie('csrf_access_token');
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
    });

    if (response.status === 401) {
      // Don't refresh on the refresh endpoint itself — that path explicitly
      // owns its own auth handling above.
      const isRefreshEndpoint = url.endsWith('/auth/refresh');
      if (allowRetry && !isRefreshEndpoint) {
        const ok = await this._attemptRefresh();
        if (ok) {
          return this._send(url, options, /*allowRetry=*/false);
        }
      }
      this._clearAuth();
      this._bounceToLogin();
      throw new ApiError(t('api.session_expired'), 401);
    }

    if (response.status === 403) {
      const ct = response.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const body = await response.clone().json();
        // i18n-ignore: matched against app_access_guard's server text, not shown
        if (body.error && body.error.includes('do not have access to this application')) {
          this._showNoAccess();
          throw new ApiError(body.error, 403, body);
        }
      }
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      if (!response.ok) {
        throw new ApiError(t('api.request_failed_text', { status: response.statusText || response.status }), response.status);
      }
      return response;
    }

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.error || data.message || t('api.request_failed_status', { status: response.status }),
        response.status,
        data
      );
    }

    return data;
  }

  _showNoAccess() {
    const meta = document.querySelector('meta[name="isg-intranet-url"]');
    const intranetUrl = (meta && meta.getAttribute('content')) || 'https://intranet.isgroup-international.com';
    document.body.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:var(--surface-primary,#f5f5f5);font-family:system-ui,sans-serif;">
        <div style="text-align:center;max-width:440px;padding:2rem;">
          <div style="font-size:3rem;margin-bottom:1rem;">&#128274;</div>
          <h1 style="font-size:1.5rem;color:var(--text-primary,#1a1a1a);margin:0 0 0.75rem;">${escText(t('api.no_access_title'))}</h1>
          <p style="color:var(--text-secondary,#666);line-height:1.6;margin:0 0 1.5rem;">${escText(t('api.no_access_body'))}</p>
          <a href="${intranetUrl}" style="display:inline-block;padding:0.6rem 1.5rem;background:var(--brand-600,#2563eb);color:#fff;border-radius:0.375rem;text-decoration:none;font-weight:500;">${escText(t('api.back_to_intranet'))}</a>
        </div>
      </div>`;
  }

  get(path, params = {}) {
    const query = new URLSearchParams(params).toString();
    const url = query ? `${path}?${query}` : path;
    return this.request(url);
  }

  post(path, body, options = {}) {
    return this.request(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      signal: options.signal,
    });
  }

  put(path, body) {
    return this.request(path, {
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  delete(path) {
    return this.request(path, { method: 'DELETE' });
  }

  patch(path, body) {
    return this.request(path, {
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  /**
   * POST a FormData upload WITH progress reporting.
   *
   * `fetch` cannot report request-upload progress, so this is the one place
   * that uses XMLHttpRequest. Use it for large files (video, big documents)
   * where a silent multi-minute wait reads as a hang; `post()` remains right
   * for everything else.
   *
   * It lives HERE rather than in a page because auth is the whole point:
   * hand-rolled uploads reach for `localStorage.jwt_token`, which SSO users
   * do not have, so the upload 401s silently for exactly the people most
   * likely to be using it. This carries the same auth as every other call —
   * `withCredentials` for the httpOnly cookie, the paired X-CSRF-TOKEN
   * header, and the legacy Authorization header when present.
   *
   *   await api.upload('/media/albums/1/items', fd, {
   *     onProgress: (pct, loaded, total) => bar.style.width = pct + '%',
   *   });
   *
   * @returns {Promise<any>} parsed JSON body (or {} for an empty response)
   */
  upload(path, formData, { onProgress, signal } = {}) {
    const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;   // httpOnly auth cookie

      if (this._token) xhr.setRequestHeader('Authorization', `Bearer ${this._token}`);
      const csrf = _readCookie('csrf_access_token');
      if (csrf) xhr.setRequestHeader('X-CSRF-TOKEN', csrf);
      const loc = _locale();
      if (loc && _sendsLocale(url)) xhr.setRequestHeader(LOCALE_HEADER, loc);
      // Deliberately NO Content-Type: the browser must set the multipart
      // boundary itself. Setting it by hand is the classic way to make an
      // upload arrive unparseable.

      if (onProgress && xhr.upload) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
          }
        });
      }

      if (signal) {
        if (signal.aborted) { xhr.abort(); return reject(new ApiError(t('api.upload_cancelled'), 0)); }
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }

      xhr.addEventListener('load', () => {
        let data = null;
        try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { data = {}; }

        if (xhr.status >= 200 && xhr.status < 300) return resolve(data);

        if (xhr.status === 401) {
          // No silent retry here: the body has already been streamed and
          // cannot be replayed after a token refresh. Surface it so the
          // caller can re-offer the file rather than pretending it uploaded.
          this._clearAuth();
          this._bounceToLogin();
          return reject(new ApiError(t('api.session_expired'), 401));
        }
        if (xhr.status === 413) {
          return reject(new ApiError(
            t('api.file_too_large'), 413, data));
        }
        reject(new ApiError(
          data?.error || t('api.upload_failed_status', { status: xhr.status }), xhr.status, data));
      });

      xhr.addEventListener('error', () => reject(new ApiError(t('api.upload_network_error'), 0)));
      xhr.addEventListener('abort', () => reject(new ApiError(t('api.upload_cancelled'), 0)));

      xhr.send(formData);
    });
  }
}

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

export const api = new ApiClient();
